import React from "react";
import { NavLink } from "react-router-dom";
import { Box, Typography, Badge, Divider } from "@mui/material";
import {
  DRAWER_WIDTH,
  SIDEBAR_FLYOUT_WIDTH,
  FLYOUT_HEADER_COLOR,
  FLYOUT_ICON_COLOR,
} from "../../constants/layout";
import { SidebarChild, SidebarSection } from "./sidebarNavConfig";

type SidebarFlyoutProps = {
  title: string;
  top: number;
  visible: boolean;
  children?: SidebarChild[];
  sections?: SidebarSection[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: () => void;
};

const FlyoutLink: React.FC<{ item: SidebarChild; onItemClick: () => void }> = ({
  item,
  onItemClick,
}) => {
  const Icon = item.icon;

  if (item.disabled) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          opacity: 0.4,
          cursor: "not-allowed",
        }}
      >
        <Icon size={18} color={FLYOUT_ICON_COLOR} />
        <Typography sx={{ fontSize: 13, color: "#333333" }}>{item.label}</Typography>
      </Box>
    );
  }

  return (
    <Box
      component={NavLink}
      to={item.to}
      onClick={onItemClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        textDecoration: "none",
        color: "#333333",
        borderRadius: "8px",
        mx: 0.5,
        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
        "&[aria-current='page']": {
          bgcolor: "rgba(30, 58, 95, 0.08)",
          fontWeight: 600,
        },
      }}
    >
      <Icon size={18} color={FLYOUT_ICON_COLOR} />
      <Typography sx={{ fontSize: 13, flex: 1, color: "#333333" }}>
        {item.label}
      </Typography>
      {item.badge ? (
        <Badge badgeContent={item.badge} color="error" sx={{ mr: 1 }} />
      ) : null}
    </Box>
  );
};

const SidebarFlyout: React.FC<SidebarFlyoutProps> = ({
  title,
  top,
  visible,
  children,
  sections,
  onMouseEnter,
  onMouseLeave,
  onItemClick,
}) => {
  if (!visible) return null;

  const renderItems = (items: SidebarChild[]) =>
    items.map((item) => (
      <FlyoutLink
        key={`${item.to}-${item.label}`}
        item={item}
        onItemClick={onItemClick}
      />
    ));

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: "fixed",
        left: DRAWER_WIDTH - 6,
        top: Math.max(8, top),
        width: SIDEBAR_FLYOUT_WIDTH,
        maxHeight: "calc(100vh - 16px)",
        overflowY: "auto",
        bgcolor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        border: "1px solid #E8E8E8",
        py: 1,
        zIndex: 1400,
      }}
    >
      <Typography
        sx={{
          px: 2,
          pt: 0.5,
          pb: 1,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: FLYOUT_HEADER_COLOR,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mx: 1.5, mb: 0.5, borderColor: "#E8E8E8" }} />

      {sections
        ? sections.map((section, idx) => (
            <Box key={section.title ?? `section-${idx}`} sx={{ mb: 0.5 }}>
              {section.title ? (
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: FLYOUT_HEADER_COLOR,
                  }}
                >
                  {section.title}
                </Typography>
              ) : null}
              {renderItems(section.items)}
            </Box>
          ))
        : children
          ? renderItems(children)
          : null}
    </Box>
  );
};

export default SidebarFlyout;
